import React, { useState, useRef, useEffect, useCallback } from "react";
import { getAddressDisplay } from "../../../../helpers/common";
import { formatCompactPrice } from "../../../../helpers/priceCapUtils";
import { BUY, LONG_RENT } from "../../../../constants/common";
import noImage from "../../../../images/no-image-placeholder.png";
import PropertyDropdownList from "../PropertyDropdownList";
import "./property_selector_dropdown.scss";

const PropertySelectorDropdown = ({
  propertyList,
  selectedProperty,
  setSelectedProperty,
  onClear,
  onUserSelect,
  adType,
  userType,
  selectedClient,
  userDetails,
  updateReloadParam,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const measureFrameRef = useRef(null);

  // The list renders in a portal on document.body, so it is not a DOM
  // descendant of this wrapper and has to be measured against the button.
  const measure = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width });
  }, []);

  // Scroll fires far faster than the browser can paint, and every call was
  // triggering a re-render (and, transitively, a re-sort/re-filter of the
  // list). Coalescing to one measure per animation frame keeps that to the
  // rate the list can actually redraw at.
  const scheduleMeasure = useCallback(() => {
    if (measureFrameRef.current) return;
    measureFrameRef.current = requestAnimationFrame(() => {
      measureFrameRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    if (!isOpen) return undefined;
    measure();
    // Capture phase so the rail's own scroll container is heard, not just window.
    window.addEventListener("scroll", scheduleMeasure, true);
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      window.removeEventListener("scroll", scheduleMeasure, true);
      window.removeEventListener("resize", scheduleMeasure);
      if (measureFrameRef.current) {
        cancelAnimationFrame(measureFrameRef.current);
        measureFrameRef.current = null;
      }
    };
  }, [isOpen, measure, scheduleMeasure]);

  // Close dropdown when clicking outside. The portalled list is outside this
  // wrapper, so a click inside it would otherwise read as "outside" and shut
  // the list before the row's own click landed.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inList = e.target?.closest?.(".property-dropdown-overlay");
      if (!inWrapper && !inList) setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!propertyList) return null;

  // Nothing is selected until the agent picks, so the control has to render an
  // empty state - otherwise there is no way to open the list and choose one.
  const addr = selectedProperty
    ? getAddressDisplay(
        selectedProperty.Location,
        selectedProperty.City,
        selectedProperty.State,
        selectedProperty.ZIP,
      )
    : { before: null, after: null };

  // Pick the right asking price based on ad type
  const askingPrice = !selectedProperty
    ? null
    : adType === BUY
      ? selectedProperty.AskingPrice_Sell
      : adType === LONG_RENT
        ? selectedProperty.AskingPrice_Rent
        : selectedProperty.AskingPrice_Night;

  return (
    <div className="property-selector-dropdown" ref={wrapperRef}>
      {/* The card is a div holding two sibling buttons: a stretched overlay
          that opens the list, and the clear control. Nesting clear inside the
          trigger is invalid HTML and put a second tab stop inside a button. */}
      <div
        ref={buttonRef}
        className={`property-selector-card ${isOpen ? "property-selector-card--open" : ""}`}
      >
        <button
          type="button"
          className="property-selector-card__main"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={
            selectedProperty
              ? `Change property, currently ${addr.before || selectedProperty.Location}`
              : "Select a property"
          }
        />
        {selectedProperty && (
          <div className="property-selector-card__thumb">
            <img src={selectedProperty.photo || noImage} alt="" />
          </div>
        )}
        <div className="property-selector-card__info">
          <span className="property-selector-card__address-line1">
            {selectedProperty
              ? addr.before || selectedProperty.Location
              : "Select a property"}
          </span>
          {addr.after && (
            <span className="property-selector-card__address-line2">
              {addr.after}
            </span>
          )}
        </div>
        {askingPrice > 0 && (
          <span className="property-selector-card__price">
            {formatCompactPrice(askingPrice)}
          </span>
        )}
        {selectedProperty ? (
          // Clearing the property is what returns the feed to the whole
          // market, so the control sits on the property rather than in a
          // separate link whose wording had to explain what it undid.
          <button
            type="button"
            aria-label="Clear property and show all Want-Listings"
            title="Clear property"
            className="property-selector-card__clear"
            onClick={() => onClear?.()}
          >
            &times;
          </button>
        ) : null}
        <span className={`property-selector-card__chevron ${isOpen ? "property-selector-card__chevron--up" : ""}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <PropertyDropdownList
          anchor={anchor}
          onRequestClose={() => setIsOpen(false)}
          propertyList={propertyList}
          selectedProperty={selectedProperty}
          setSelectedProperty={(property) => {
            // Picking the row that is already selected clears it, so a
            // property is never a one-way trip. Goes through onClear rather
            // than calling setSelectedProperty(null) directly, so any
            // cleanup onClear does beyond clearing the property (e.g. the
            // parent's own state) also applies on this path.
            const same =
              selectedProperty &&
              property &&
              selectedProperty.Inv_Id === property.Inv_Id &&
              selectedProperty.ListingKey === property.ListingKey;
            if (same) {
              if (onClear) onClear();
              else setSelectedProperty(null);
            } else {
              setSelectedProperty(property);
              if (onUserSelect) onUserSelect(property);
            }
            setIsOpen(false);
          }}
          adType={adType}
          userType={userType}
          selectedClient={selectedClient}
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
        />
      )}
    </div>
  );
};

export default PropertySelectorDropdown;
